"use client";

import { useTranslations } from "next-intl";
import { useContext, useEffect, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Image from "@/components/ui/Image/Image";
import { ModalsContext } from "@/context/ModalsContext";
import { CustomLink } from "@/types";
import cn from "@/utils/cn";
import { countdownToEvent } from "@/utils/countdownToEvent";
import { formatDate } from "@/utils/dateUtils";

export interface EventHeroProps {
  tag?: string;
  title: string;
  eventDate: string;
  location: string;
  countdownEndDate?: string;
  backgroundImage: { src: string; alt: string };
  registerHref?: string;
  locale?: string;
  /** CMS-driven label for the "Get in Touch" CTA shown once the event has ended. */
  cta?: CustomLink;
  eventExpireText?: string;
  timeRemaining?: string;
}

export const CountdownUnit = ({ value, label }: { value: number; label: string }) => (
  <div className="content-stretch flex flex-col items-center font-primary">
    <p className="text-white text-xl tracking-[0.48px]">{String(value).padStart(2, "0")}</p>
    <p className="text-gold-10 text-xs tracking-[1px] uppercase mt-xs">{label}</p>
  </div>
);

type EventStatus = "unknown" | "upcoming" | "live" | "ended";

// Comparisons use epoch ms (Date#getTime), which is timezone-agnostic: both `now` and the
// content dates resolve to the same absolute instant regardless of the viewer's local timezone.
const getEventStatus = (eventDate: string, countdownEndDate?: string): EventStatus => {
  const eventTime = new Date(eventDate).getTime();
  if (Number.isNaN(eventTime)) return "unknown";

  const now = Date.now();
  if (now < eventTime) return "upcoming";

  const rawEndTime = countdownEndDate ? new Date(countdownEndDate).getTime() : NaN;
  // If countdownEndDate is missing or misconfigured (at/before eventDate), treat the event as
  // having no expiry rather than snapping straight to "ended" the moment it goes live.
  const hasValidEndTime = !Number.isNaN(rawEndTime) && rawEndTime > eventTime;
  if (!hasValidEndTime) return "live";

  return now < rawEndTime ? "live" : "ended";
};

const safeCountdown = (eventDate: string) =>
  Number.isNaN(new Date(eventDate).getTime()) ? { days: 0, hours: 0, minutes: 0, seconds: 0 } : countdownToEvent(new Date(eventDate));

const EventHero = ({
  tag,
  title,
  eventDate,
  location,
  countdownEndDate,
  backgroundImage,
  registerHref = "#event-register",
  locale = "en",
  cta,
  eventExpireText,
  timeRemaining,
}: EventHeroProps) => {
  const t = useTranslations();
  const [status, setStatus] = useState(() => getEventStatus(eventDate, countdownEndDate));
  const [timeLeft, setTimeLeft] = useState(() => safeCountdown(eventDate));
  const { setGetInTouchModalOpen, setGetFormPageLocation } = useContext(ModalsContext);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setStatus(getEventStatus(eventDate, countdownEndDate));
      setTimeLeft(safeCountdown(eventDate));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [eventDate, countdownEndDate]);

  const scrollToRegister = () => {
    document.querySelector(registerHref)?.scrollIntoView({ behavior: "smooth" });
  };

  const openGetInTouch = () => {
    setGetFormPageLocation("EventHero");
    setGetInTouchModalOpen(true);
  };

  const isValidEventDate = status !== "unknown";

  return (
    <section className="relative w-full h-svh min-h-[560px] overflow-hidden">
      <Image src={backgroundImage.src} alt={backgroundImage.alt} width={1920} height={1080} className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black-0/90 from-30% to-black-0/0" />

      <div className="relative z-10 flex h-full flex-col justify-center px-l md:px-xxl">
        <div className="grid w-full max-w-[1440px] mx-auto grid-cols-1 md:grid-cols-2 gap-xl items-start">
          <div className="flex flex-col gap-xl items-start">
            {isValidEventDate && (
              <span className="font-primary golden-gradient rounded p-xxs text-black text-xs font-normal">
                {formatDate(eventDate, locale).toUpperCase()}
              </span>
            )}

            {title && <h1 className="font-primary text-white text-xl tracking-[-1.6px] max-w-[250px]">{title}</h1>}

            <div className="border-t border-[rgba(78,70,57,0.3)] flex items-center gap-xxl w-full pt-xs">
              {isValidEventDate && (
                <div className="flex flex-col gap-xs">
                  <p className="text-gold-10 text-s tracking-[1.8px] uppercase opacity-75 font-normal">Date</p>
                  <p className="font-primary text-white text-md">{formatDate(eventDate, locale).toUpperCase()}</p>
                </div>
              )}
              {location && (
                <div className="flex flex-col gap-xs">
                  <p className="font-primary text-gold-10 text-xs tracking-[1.8px] uppercase font-normal opacity-75">{t("search.label.location")}</p>
                  <p className="font-primary text-white text-md font-normal uppercase">{location}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-stretch md:items-end">
            <div className="backdrop-blur-md bg-[rgba(42,42,42,0.6)] border border-[rgba(78,70,57,0.2)] rounded-2xl flex flex-col gap-l p-xl w-full md:max-w-[448px]">
              {status === "ended" ? (
                <>
                  {eventExpireText && <p className="text-gold font-primary text-s tracking-[1.8px] uppercase">{eventExpireText}</p>}
                  <Button
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.WHITE}
                    rounded
                    customClass={cn("w-full uppercase tracking-[1.8px]")}
                    onClick={openGetInTouch}
                  >
                    {cta?.label || "Get in Touch"}
                  </Button>
                </>
              ) : status === "live" ? (
                <>
                  {eventExpireText && <p className="text-gold font-primary text-s tracking-[1.8px] uppercase">{eventExpireText}</p>}
                  <Button
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.WHITE}
                    rounded
                    customClass={cn("w-full uppercase tracking-[1.8px]")}
                    onClick={scrollToRegister}
                  >
                    {cta?.label || "Register"}
                  </Button>
                </>
              ) : status === "upcoming" ? (
                <>
                  {timeRemaining && <p className="text-gold font-primary text-s tracking-[1.8px] uppercase">{timeRemaining}</p>}
                  <div className="flex items-center justify-between gap-xs">
                    <CountdownUnit value={timeLeft.hours} label="Hours" />
                    <span className="text-gold text-xl">:</span>
                    <CountdownUnit value={timeLeft.minutes} label="Mins" />
                    <span className="text-gold text-xl">:</span>
                    <CountdownUnit value={timeLeft.seconds} label="Secs" />
                  </div>
                  <Button
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.WHITE}
                    rounded
                    customClass={cn("w-full uppercase tracking-[1.8px]")}
                    onClick={scrollToRegister}
                  >
                    {cta?.label || "Register"}
                  </Button>
                </>
              ) : (
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.WHITE}
                  rounded
                  customClass={cn("w-full uppercase tracking-[1.8px]")}
                  onClick={scrollToRegister}
                >
                  {cta?.label || "Register"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventHero;
